"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  Plus, 
  Search, 
  Users, 
  UserCheck, 
  UserPlus, 
  MoreHorizontal, 
  Mail, 
  Phone,
  Download,
  Filter
} from "lucide-react";

const customers = [
  { 
    id: "CUST-001", 
    name: "John Doe", 
    email: "john.doe@example.com", 
    phone: "+234 800 123 4567",
    status: "Active", 
    joined: "2 days ago",
    totalSpend: "₦125,000"
  },
  { 
    id: "CUST-002", 
    name: "Sarah Williams", 
    email: "sarah.w@acme.inc", 
    phone: "+234 811 222 3333",
    status: "Active", 
    joined: "1 week ago",
    totalSpend: "₦450,200"
  },
  { 
    id: "CUST-003", 
    name: "Michael Chen", 
    email: "m.chen@global.co", 
    phone: "+234 703 444 5555",
    status: "Inactive", 
    joined: "1 month ago",
    totalSpend: "₦0"
  },
  { 
    id: "CUST-004", 
    name: "Aisha Mohammed", 
    email: "aisha.m@tech.ng", 
    phone: "+234 905 666 7777",
    status: "Active", 
    joined: "3 months ago",
    totalSpend: "₦2,100,000"
  },
  { 
    id: "CUST-005", 
    name: "Robert Fox", 
    email: "robert.fox@fox.net", 
    phone: "+234 802 888 9999",
    status: "Blocked", 
    joined: "5 months ago",
    totalSpend: "₦12,500"
  },
];

const stats = [
  {
    title: "Total Customers",
    value: "1,284",
    description: "+12.5% from last month",
    icon: Users,
    color: "text-blue-600",
    bg: "bg-blue-100 dark:bg-blue-500/10"
  },
  {
    title: "Active Customers",
    value: "1,142",
    description: "89% engagement rate",
    icon: UserCheck,
    color: "text-emerald-600",
    bg: "bg-emerald-100 dark:bg-emerald-500/10"
  },
  {
    title: "New Customers",
    value: "+48",
    description: "Last 7 days",
    icon: UserPlus,
    color: "text-amber-600",
    bg: "bg-amber-100 dark:bg-amber-500/10"
  }
];

import { AddCustomerModal } from "@/components/modals/add-customer-modal";

export default function CustomersPage() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1">Manage your customer relationships and transaction history.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <AddCustomerModal>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus className="mr-2 h-4 w-4" />
              Add Customer
            </Button>
          </AddCustomerModal>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <div className={`${stat.bg} ${stat.color} p-2 rounded-lg`}>
                <stat.icon className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stat.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search customers..." className="pl-9" />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="w-[250px]">Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Total Spend</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-muted/50 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-medium shrink-0">
                          {customer.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">{customer.name}</span>
                          <span className="text-xs text-muted-foreground">{customer.id}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center text-xs text-muted-foreground">
                          <Mail className="mr-1 h-3 w-3" />
                          {customer.email}
                        </div>
                        <div className="flex items-center text-xs text-muted-foreground">
                          <Phone className="mr-1 h-3 w-3" />
                          {customer.phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "rounded-full font-normal",
                          customer.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20" :
                          customer.status === "Blocked" ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20" :
                          "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                        )}
                      >
                        {customer.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {customer.joined}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {customer.totalSpend}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between space-x-2 py-4">
            <div className="text-sm text-muted-foreground">
              Showing <strong>1-5</strong> of <strong>1,284</strong> customers
            </div>
            <div className="space-x-2">
              <Button variant="outline" size="sm" disabled>Previous</Button>
              <Button variant="outline" size="sm">Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
